import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const genericFeatures = [
  '能做摘要',
  '能给泛泛建议',
  '像一个会总结的助手',
];

const professionalFeatures = [
  { label: '切片', desc: '先切片，再识别多模态信息', icon: 'ri-scissors-cut-line' },
  { label: '指标映射', desc: '把片段映射到思政课评价指标', icon: 'ri-map-pin-2-line' },
  { label: '专家样本', desc: '根据专家标注样本和训练后专用模型', icon: 'ri-database-2-line' },
  { label: '总分分档', desc: '输出总分、维度分、分档结果', icon: 'ri-bar-chart-grouped-line' },
  { label: '证据链', desc: '结果+证据同时出现，可解释', icon: 'ri-links-line' },
  { label: '趋势图', desc: '支持一校一图、一省一图的质量趋势', icon: 'ri-line-chart-line' },
];

export default function P22ModelComparison({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span className={`inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            能力差异
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            普通大模型点评<span className="text-foreground-300 mx-2">vs</span><span className="text-primary-600">专业评课模型</span>
          </h2>
          <p className={`text-base text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            通用模型像一个会总结的助手，专业评课模型像一个经过标准训练、能出评价结果、能解释依据的评价系统
          </p>
        </div>

        {/* Side by side comparison */}
        <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Generic model */}
          <div className="rounded-2xl glass border-white/50 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-foreground-100/50 border border-foreground-200/30 flex items-center justify-center">
                <i className="ri-chat-3-line text-xl text-foreground-500" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground-700">通用大模型</h3>
                <p className="text-xs text-foreground-400 font-body">把视频丢进去让它"点评一下"</p>
              </div>
            </div>

            <div className="space-y-3">
              {genericFeatures.map((feat) => (
                <div key={feat} className="flex items-center gap-3 p-3 rounded-xl bg-background-100/50">
                  <div className="w-7 h-7 rounded-lg bg-foreground-100/60 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xs text-foreground-500" />
                  </div>
                  <span className="text-sm font-body text-foreground-600">{feat}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 rounded-xl bg-red-50/30 border border-red-100/30">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-close-circle-line text-sm text-red-400" />
                <span className="text-xs font-body font-semibold text-red-500">局限</span>
              </div>
              <p className="text-xs text-foreground-500 font-body leading-relaxed">
                不能稳定、准确、可验证地评价思政课。没有统一指标、没有专家样本、没有证据链、没有维度分和分档。
              </p>
            </div>
          </div>

          {/* Professional model */}
          <div className="rounded-2xl glass-liquid border-primary-200/30 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6 relative z-10">
              <div className="w-12 h-12 rounded-2xl glass-blue border-primary-200/40 flex items-center justify-center">
                <i className="ri-brain-line text-xl text-primary-600" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-foreground-800">专业评课模型</h3>
                <p className="text-xs text-foreground-500 font-body">经过标准训练 · 能出结果 · 能解释依据</p>
              </div>
              <div className="ml-auto">
                <span className="px-3 py-1 rounded-full glass-gold border-accent-200/30 text-xs font-body font-semibold text-accent-600">云智评</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 relative z-10">
              {professionalFeatures.map((feat, i) => (
                <div key={feat.label} className="flex items-start gap-3 p-3 rounded-xl glass-blue border-primary-200/30">
                  <div className="w-7 h-7 rounded-lg bg-primary-100/60 border border-primary-200/40 flex items-center justify-center flex-shrink-0">
                    <i className={`${feat.icon} text-xs text-primary-600`} />
                  </div>
                  <div>
                    <p className="text-sm font-body font-semibold text-foreground-800">{feat.label}</p>
                    <p className="text-xs text-foreground-500 font-body">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 rounded-xl bg-accent-50/30 border border-accent-200/30 relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-checkbox-circle-line text-sm text-accent-600" />
                <span className="text-xs font-body font-semibold text-accent-700">优势</span>
              </div>
              <p className="text-xs text-foreground-600 font-body leading-relaxed">
                按标准评价、按样本训练、按结果验证。能稳定输出总分、维度分、分档和证据链，支持大规模常态化应用。
              </p>
            </div>
          </div>
        </div>

        <div className={`mt-6 text-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl glass border-white/50">
            <i className="ri-information-line text-accent-500" />
            <span className="text-sm font-body text-foreground-600">
              关键差异：不是<strong className="text-foreground-500">"能不能写点评"</strong>，而是<strong className="text-primary-700">"能不能稳定、准确、可验证地评课"</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}